import * as Api from '@ViewModels';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { ISocialMediaConnectResult } from '../../../models/SocialMedia';
import {
	getSocialMediaConnectionErrorMessage,
	useFinishSocialMediaConnectionMutation,
	useSetSocialMediaPostTargetMutation,
} from '../../../queries/Social/useSocialMediaConnectionMutations';
import { IModalProps } from '../Modal';
import {
	SocialMediaConnectionSuccessModal,
	SocialMediaPostTargetSelectorModal,
} from '../settings/PersonalSettings/SocialMediaSettings/presentation';

/**
 * Global provider that handles social media connection success notifications.
 * This provider monitors the URL for social media OAuth callback results and
 * displays the success modal from anywhere in the app, not just from settings.
 * It also handles post target selection when multiple options are available.
 */
export const SocialMediaConnectionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const location = useLocation<ISocialMediaConnectResult>();
	const history = useHistory();
	const userSession = useUserSession();
	const finishConnection = useFinishSocialMediaConnectionMutation();
	const setPostTarget = useSetSocialMediaPostTargetMutation();
	const [isChoosingPostTarget, setIsChoosingPostTarget] = React.useState(false);

	const [connectionSuccessModalProps, setConnectionSuccessModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: Api.VmUtils.Noop,
	});

	const showSuccessModal = React.useCallback(() => {
		setConnectionSuccessModalProps({
			isOpen: true,
			onRequestClose: () => {
				setConnectionSuccessModalProps({
					isOpen: false,
					onRequestClose: Api.VmUtils.Noop,
				});
			},
		});
	}, []);

	const { mutate: finish } = finishConnection;
	const finishPendingConnection = React.useCallback(
		(result: ISocialMediaConnectResult) => {
			if (!Api.SupportedSocialMediaTypes.includes(result.type)) {
				toast.error('Unsupported social media type');
				return;
			}

			const pendingConnections =
				userSession?.user?.socialMediaConnectedAccounts?.filter(
					x =>
						x?.type === result.type &&
						x.encryptedBlobId &&
						x.state !== Api.SocialMediaConnectionState.Connected &&
						(result.userId ? x.userId === result.userId : true)
				) ?? [];
			const connection = pendingConnections.length ? pendingConnections[pendingConnections.length - 1] : null;

			finish(
				{ type: result.type, connection },
				{
					onError: error =>
						toast.error(getSocialMediaConnectionErrorMessage(error, 'Failed to load post targets. Please try again.')),
					onSuccess: ({ pages }) => {
						if (pages.length === 0) {
							toast.error(
								'There are no post targets to choose from for the given account. Please add a page or choose a different account to connect to.'
							);
						} else if (pages.length === 1) {
							showSuccessModal();
						} else {
							setIsChoosingPostTarget(true);
						}
					},
				}
			);
		},
		[finish, userSession, showSuccessModal]
	);

	const onPostTargetChosen = (page?: Api.ISocialMediaPage, cancel?: boolean) => {
		setIsChoosingPostTarget(false);
		if (!page || cancel || !finishConnection.data || !finishConnection.variables) {
			return;
		}

		setPostTarget.mutate(
			{
				type: finishConnection.variables.type,
				page,
				connection: finishConnection.variables.connection,
				accountId: finishConnection.data.accountId,
			},
			{
				onError: error =>
					toast.error(getSocialMediaConnectionErrorMessage(error, 'Failed to set post target. Please try again.')),
				onSuccess: () => showSuccessModal(),
			}
		);
	};

	React.useEffect(() => {
		// Check if this is a social media connection result callback
		const searchParams = new URLSearchParams(location.search);

		if (searchParams.get('op') === 'connect-result') {
			// Get result from location.state if available, otherwise from query params
			let result: ISocialMediaConnectResult;

			if (location.state) {
				result = location.state as ISocialMediaConnectResult;
			} else {
				// Fallback: construct result from query params (for manual URL navigation or if state is lost)
				const success = searchParams.get('success')?.toLowerCase() === 'true';
				const errorMessage = searchParams.get('errorMessage') || undefined;
				const userId = searchParams.get('userId') || undefined;
				const type = searchParams.get('type') || undefined;

				result = {
					success,
					errorMessage,
					userId,
					type: type as Api.SocialMediaType,
				};
			}

			// Only handle post target selection if the connection was successful
			if (result.success) {
				finishPendingConnection(result);
			} else {
				// Show error notification for failed connections
				const errorMsg = result.errorMessage || 'Failed to connect social media account. Please try again.';
				toast.error(errorMsg);
			}

			// Clean up the URL by removing the query parameter and state
			// This prevents the modal from showing again on page refresh
			history.replace({
				pathname: location.pathname,
				search: '', // Remove all query params
				state: null,
			});
		}
	}, [location, history, finishPendingConnection]);

	return (
		<>
			{children}
			<SocialMediaConnectionSuccessModal modalProps={connectionSuccessModalProps} />
			{finishConnection.data && finishConnection.variables ? (
				<SocialMediaPostTargetSelectorModal
					modalProps={{ isOpen: isChoosingPostTarget, onRequestClose: onPostTargetChosen }}
					pages={finishConnection.data.pages}
					type={finishConnection.variables.type}
				/>
			) : null}
		</>
	);
};
